import { FunnelId } from 'constants/funnel';
import clsx from 'clsx';
import Image from 'components/ui/Image';
import Stars from 'components/ui/Stars';
import { sanitizeContentfulBlockId } from 'utils/contentful';
import { quoteWrap } from 'utils/quoteWrap';
import { useAnalytics } from 'hooks/useAnalytics';
import Button from 'components/Qds/Button';
import ExternalFunnelButton from 'components/Shared/ExternalFunnel/ExternalFunnelButton';
import type { IBlockGenericCtaFields } from 'types/contentful/generated';

type Props = IBlockGenericCtaFields & {
  className?: string;
};

const GenericCTASmall = ({
  ctaText,
  patientManagerAvatar,
  patientManagerName,
  patientManagerTitle,
  patientManagerMessage,
  reviewStars,
  reviewText,
  reviewAuthor,
  reviewAuthorCountry,
  theme,
  id,
  className = 'mx-auto px-3',
}: Props): JSX.Element => {
  const { trackEvent } = useAnalytics();

  return (
    <>
      <div
        id={sanitizeContentfulBlockId(id)}
        className={clsx('max-w-screen-xl h-full', className)}
      >
        <div
          className="flex flex-col pb-6 px-4 rounded-md border border-silver-light h-full text-primary"
          style={{ backgroundColor: 'rgba(231, 226, 214, 0.5)' }}
        >
          <div className="flex flex-col py-4 col-span-2 h-48 mt-2 rating border-b border-infobox">
            <Stars
              className="text-tertiary mb-2"
              rating={reviewStars}
              starSize={13}
            />
            <span className="mb-3 text-sm">
              <span
                className={clsx(
                  'font-bold',
                  'Dark' === theme ? 'text-white' : 'text-grey-darkest',
                )}
              >
                {reviewAuthor}
              </span>
              <span className={clsx('Dark' === theme && 'text-silver-light')}>
                , {reviewAuthorCountry}:
              </span>
              <br />
              <span
                className={clsx(
                  'md:text-base',
                  'Dark' === theme && 'text-charcoal',
                )}
              >
                {quoteWrap(reviewText)}
              </span>
            </span>
          </div>
          <div className="mt-2">
            <div className="flex items-center">
              {patientManagerAvatar && (
                <Image
                  className="w-10 rounded-full mr-4"
                  src={patientManagerAvatar.fields.file.url}
                  alt={patientManagerAvatar.fields.description}
                  width={40}
                  height={40}
                />
              )}
              <div className="ml-2">
                <p
                  className={clsx(
                    'font-semibold text-xs',
                    'Dark' === theme ? 'text-white' : 'text-grey-darkest',
                  )}
                >
                  {patientManagerName}
                </p>
                <p
                  className={clsx(
                    'text-center text-xs',
                    'Dark' === theme && 'text-silver-light',
                  )}
                >
                  {patientManagerTitle}
                </p>
              </div>
            </div>
            <div className="text-center pt-4">
              <div className="relative rounded-lg italic">
                {patientManagerMessage}
              </div>
              <ExternalFunnelButton
                funnelId={FunnelId.CONTACT_FORM}
                renderButton={(openFunnel) => {
                  return (
                    <Button
                      className="mt-4"
                      fullWidth
                      onClick={() => {
                        trackEvent({
                          category: 'CTA',
                          action: 'CTA|Click',
                          label: `CTA|GEO|ClinicCard|Get in Touch`,
                        });

                        openFunnel();
                      }}
                    >
                      {ctaText}
                    </Button>
                  );
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default GenericCTASmall;
